import React from 'react'
import { useLanguageContext } from '../../contexts/LanguageContext'
import './Roadmap.css'
import { motion, AnimatePresence } from "framer-motion";
import SEO from '../seo/SEO';

const Roadmap: React.FC = () => {
  const { content } = useLanguageContext()
  const [activePhase, setActivePhase] = React.useState<number | null>(null);
  const phaseRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  React.useEffect(() => {
    if (activePhase === null) return;

    const index = activePhase - 1;
    const el = phaseRefs.current[index];

    if (!el) return;

    requestAnimationFrame(() => {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    });
  }, [activePhase]);

  return (
    <>
      <SEO
        title={content.roadmap.seo.title}
        description={content.roadmap.seo.description}
      />
      <section id="roadmap" className="roadmap-section">
        <div className="container">
          <div className="roadmap-header text-center">
            <p className="section-subtitle">{content.roadmap.subtitle}</p>
            <h2 className="section-title">{content.roadmap.title}</h2>
            <p className="section-description">{content.roadmap.description}</p>
          </div>
            {/* CURVED SVG + MARKERS */}
            <div className="roadmap-path-wrapper">
              <svg viewBox="0 0 1200 300" width="100%" height="100%" preserveAspectRatio="none">
                <path
                  d="M 0 150 C 220 50, 420 220, 700 150 C 900 80, 1100 200, 1300 10"
                  stroke="#1D2942"
                  strokeWidth="70"
                  fill="none"
                  strokeLinecap="round"
                />
                <path
                  d="M 0 150 C 220 50, 420 220, 700 150 C 900 80, 1100 200, 1300 10"
                  stroke="white"
                  strokeWidth="8"
                  strokeDasharray="25 20"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>

              {content.roadmap.phases.map((p: any, index: number) => {
                const phaseId = index + 1;
                return (<motion.div
                  key={phaseId}
                  onClick={() => setActivePhase(phaseId) }
                  className="marker"
                  style={{ left: p.pin.left, top: p.pin.top }}
                  initial={{ opacity: 0, scale: 0.4 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.2 }}
                  transition={{ type: "spring", stiffness: 180, damping: 12, delay: phaseId * 0.15 }}
                >
                  <div className="marker-circle" style={{ backgroundColor: p.pin.color }}>
                    {phaseId}
                  </div>
                  <div
                    className="marker-pointer"
                    style={{ borderTopColor: p.pin.color }}
                  ></div>
                </motion.div>
              )})}
            </div>

            {/* PHASE PANELS */}
            <div className="phases-grid">
              {content.roadmap.phases.map((p: any, index: number) => {
                const phaseId = index + 1;
                const isActive = activePhase === phaseId ;

                return (
                  <div key={phaseId} ref={(el) => (phaseRefs.current[phaseId - 1] = el)}>
                    <motion.div
                      onClick={() => setActivePhase(phaseId)}
                      className={`phase-card ${isActive ? "active" : ""}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: phaseId * 0.15 + 0.3 }}
                    >
                      <h3 className="phase-title">{p.title}</h3>

                      <AnimatePresence>
                        {isActive && (
                          <motion.div
                            key="content"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="phase-content"
                          >
                            <p>{p.description}</p>

                            <ul>
                              {p.features.map((feature: any, i: number) => {
                                const dataStatus =
                                  feature.status === "done"
                                    ? "Completed"
                                    : feature.status === "in-progress"
                                    ? "In Progress"
                                    : "Planned";

                                return (
                                  <li
                                    key={i}
                                    className={`roadmap-feature-item ${feature.status}`}
                                    data-status={dataStatus}
                                  >
                                    <span className="roadmap-feature-icon"></span>
                                    <span>{feature.text}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </div>
                );
              })}
            </div>
        </div>
      </section>
    </>
  )
}

export default Roadmap 